import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2 } from 'lucide-react';
// Configure PDF.js worker - use CDN for the worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
// Loading spinner component
function LoadingSpinner({ className, size = 'md' }) {
    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-6 h-6',
        lg: 'w-8 h-8'
    };
    return (_jsx("div", { className: `flex items-center justify-center ${className || ''}`, children: _jsx(Loader2, { className: `${sizeClasses[size]} animate-spin text-muted-foreground` }) }));
}
export function PdfPageRenderer({ pdfUrl, pageNumber, width, className, renderTextLayer = false, renderAnnotationLayer = false, onLoadSuccess, onError }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const handleLoadSuccess = ({ numPages }) => {
        setLoading(false);
        onLoadSuccess?.(numPages);
    };
    const handleError = (err) => {
        setLoading(false);
        setError(err);
        onError?.(err);
    };
    if (error) {
        return (_jsx("div", { className: `flex items-center justify-center text-destructive text-sm ${className || ''}`, children: "Failed to load PDF" }));
    }
    return (_jsxs("div", { className: className, children: [loading && (_jsx(LoadingSpinner, { className: "py-4", size: "md" })), _jsx(Document, { file: pdfUrl, onLoadSuccess: handleLoadSuccess, onLoadError: handleError, loading: null, children: _jsx(Page, { pageNumber: pageNumber, width: width, renderTextLayer: renderTextLayer, renderAnnotationLayer: renderAnnotationLayer, loading: _jsx(LoadingSpinner, { className: "py-4", size: "sm" }) }) })] }));
}
const SharedPdfContext = createContext(null);
/**
 * Provider that loads a PDF once using a single Document component.
 * Children receive a renderPage function to render pages inside the Document.
 */
export function SharedPdfProvider({ pdfUrl, urlLoading = false, children, onLoadSuccess }) {
    const [numPages, setNumPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageDimensions, setPageDimensions] = useState(null);
    const handleLoadSuccess = async (pdf) => {
        setNumPages(pdf.numPages);
        onLoadSuccess?.(pdf.numPages);
        try {
            const page = await pdf.getPage(1);
            const viewport = page.getViewport({ scale: 1 });
            setPageDimensions({
                width: viewport.width,
                height: viewport.height,
                aspectRatio: viewport.width / viewport.height
            });
        }
        catch (err) {
            console.error('Failed to get page dimensions:', err);
        }
        setLoading(false);
    };
    const handleError = (err) => {
        setLoading(false);
        setError(err);
    };
    const isLoading = urlLoading || (pdfUrl ? loading : true);
    const value = {
        pdfUrl,
        numPages,
        loading: isLoading,
        error,
        pageDimensions
    };
    // Render function that children use to render pages
    const renderPage = (pageNumber, width) => (_jsx(Page, { pageNumber: pageNumber, width: width, renderTextLayer: false, renderAnnotationLayer: false, loading: _jsx(LoadingSpinner, { className: "py-4", size: "sm" }) }, pageNumber));
    if (error) {
        return (_jsx("div", { className: "flex items-center justify-center text-destructive text-sm py-4", children: "Failed to load PDF" }));
    }
    return (_jsx(SharedPdfContext.Provider, { value: value, children: pdfUrl ? (_jsx(Document, { file: pdfUrl, onLoadSuccess: handleLoadSuccess, onLoadError: handleError, loading: _jsx(LoadingSpinner, { className: "py-4", size: "md" }), children: children(renderPage) })) : (_jsx(LoadingSpinner, { className: "py-4", size: "md" })) }));
}
export function useSharedPdf() {
    return useContext(SharedPdfContext);
}
// A4 portrait aspect ratio
const A4_ASPECT_RATIO = 210 / 297;
/**
 * Simple wrapper for a PDF page that adds styling.
 * Must be used inside SharedPdfProvider's children render function.
 */
export function SimplePdfPage({ pageNumber, width, className }) {
    const context = useSharedPdf();
    const aspectRatio = context?.pageDimensions?.aspectRatio ?? A4_ASPECT_RATIO;
    const placeholderHeight = width ? Math.round(width / aspectRatio) : 200;
    if (context?.loading) {
        return (_jsx("div", { className: `flex items-center justify-center bg-muted ${className || ''}`, style: { height: placeholderHeight, width: width || '100%' }, children: _jsx(LoadingSpinner, { size: "md" }) }));
    }
    return (_jsx("div", { className: className, children: _jsx(Page, { pageNumber: pageNumber, width: width, renderTextLayer: false, renderAnnotationLayer: false, loading: _jsx(LoadingSpinner, { className: "py-4", size: "sm" }) }) }));
}
/**
 * Virtualized PDF thumbnail that only renders the Page when visible.
 * Uses IntersectionObserver for efficient visibility detection.
 */
function VirtualizedThumbnail({ pageNumber, width, isSelected, onSelect, renderThumbnail, aspectRatio = A4_ASPECT_RATIO, rootMargin = '200px 0px' }) {
    const containerRef = useRef(null);
    const [hasBeenVisible, setHasBeenVisible] = useState(false);
    // Set up intersection observer
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        const observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (entry?.isIntersecting) {
                setHasBeenVisible(true);
            }
        }, { rootMargin, threshold: 0 });
        observer.observe(container);
        return () => observer.disconnect();
    }, [rootMargin]);
    const placeholderHeight = width ? Math.round(width / aspectRatio) : 200;
    // Only render the actual Page component if visible or has been visible
    // Once rendered, keep it rendered to preserve the canvas
    const shouldRenderPage = hasBeenVisible;
    const pageElement = shouldRenderPage ? (_jsx(Page, { pageNumber: pageNumber, width: width, renderTextLayer: false, renderAnnotationLayer: false, loading: _jsx("div", { className: "flex items-center justify-center bg-muted", style: { height: placeholderHeight }, children: _jsx(LoadingSpinner, { size: "sm" }) }) })) : (_jsx("div", { className: "flex items-center justify-center bg-muted", style: { height: placeholderHeight, width: width || '100%' }, children: _jsx("span", { className: "text-muted-foreground text-xs", children: pageNumber }) }));
    return (_jsx("div", { ref: containerRef, children: renderThumbnail({
            pageNumber,
            isSelected,
            pageElement,
            onSelect
        }) }));
}
/**
 * Renders a list of PDF page thumbnails using a single Document.
 * Uses windowed virtualization for better performance with large PDFs.
 * Only renders components for pages within a window around the current scroll position.
 */
// Helper to find the scrollable ancestor element
function findScrollableAncestor(element) {
    if (!element)
        return null;
    let current = element.parentElement;
    while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
            return current;
        }
        current = current.parentElement;
    }
    return null;
}
export function PdfThumbnailList({ pdfUrl, urlLoading = false, pageCount, currentPage, thumbnailWidth, onPageSelect, renderThumbnail, scrollContainerRef, onAspectRatioChange, onItemHeightChange, calculateItemHeight, onPageCountChange }) {
    const [error, setError] = useState(null);
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: Math.min(15, pageCount) });
    // Start with null to indicate we haven't loaded the PDF yet
    const [aspectRatio, setAspectRatio] = useState(null);
    const containerRef = useRef(null);
    const handleError = useCallback((err) => {
        setError(err);
    }, []);
    // Get actual page dimensions and count from PDF on load
    const handleLoadSuccess = useCallback(async (pdf) => {
        // Report actual page count from PDF
        onPageCountChange?.(pdf.numPages);
        try {
            const page = await pdf.getPage(1);
            const viewport = page.getViewport({ scale: 1 });
            const ratio = viewport.width / viewport.height;
            setAspectRatio(ratio);
            onAspectRatioChange?.(ratio);
        }
        catch (err) {
            console.error('Failed to get page dimensions:', err);
            // Fall back to A4 if we can't get dimensions
            setAspectRatio(A4_ASPECT_RATIO);
            onAspectRatioChange?.(A4_ASPECT_RATIO);
        }
    }, [onAspectRatioChange, onPageCountChange]);
    // Use A4 as fallback if aspect ratio not yet determined
    const effectiveAspectRatio = aspectRatio ?? A4_ASPECT_RATIO;
    // Calculate placeholder height using actual aspect ratio from PDF
    const placeholderHeight = thumbnailWidth ? Math.round(thumbnailWidth / effectiveAspectRatio) : 200;
    // Total height per item - use custom calculator if provided, otherwise default formula
    // Default: padding (p-2 = 8px top + 8px bottom) + page number text (~24px) + gap
    const itemHeight = calculateItemHeight
        ? calculateItemHeight(placeholderHeight)
        : placeholderHeight + 16 + 24 + 8;
    // Notify parent of item height changes for scroll calculations
    useEffect(() => {
        if (itemHeight > 0 && aspectRatio !== null) {
            onItemHeightChange?.(itemHeight);
        }
    }, [itemHeight, aspectRatio, onItemHeightChange]);
    // Window size: how many pages to render above and below visible area
    const WINDOW_BUFFER = 5;
    // Track scroll position to update visible range
    useEffect(() => {
        // Find the scroll container - either from prop or by searching ancestors
        const container = scrollContainerRef?.current || findScrollableAncestor(containerRef.current);
        if (!container)
            return;
        const updateVisibleRange = () => {
            const scrollTop = container.scrollTop;
            const viewportHeight = container.clientHeight;
            // Calculate which pages are visible
            const firstVisible = Math.floor(scrollTop / itemHeight);
            const lastVisible = Math.ceil((scrollTop + viewportHeight) / itemHeight);
            // Add buffer around visible range
            const start = Math.max(0, firstVisible - WINDOW_BUFFER);
            const end = Math.min(pageCount, lastVisible + WINDOW_BUFFER);
            setVisibleRange(prev => {
                if (prev.start !== start || prev.end !== end) {
                    return { start, end };
                }
                return prev;
            });
        };
        // Initial calculation
        updateVisibleRange();
        // Listen to scroll events
        container.addEventListener('scroll', updateVisibleRange, { passive: true });
        return () => container.removeEventListener('scroll', updateVisibleRange);
    }, [itemHeight, pageCount, scrollContainerRef]);
    if (error) {
        return (_jsx("div", { className: "flex items-center justify-center text-destructive text-sm py-4", children: "Failed to load PDF" }));
    }
    if (urlLoading || !pdfUrl) {
        return _jsx(LoadingSpinner, { className: "py-4", size: "md" });
    }
    // Calculate spacer heights for virtual scrolling
    const topSpacerHeight = visibleRange.start * itemHeight;
    const bottomSpacerHeight = (pageCount - visibleRange.end) * itemHeight;
    // Only render the virtualized list once we have the actual aspect ratio
    // This prevents the total scroll height from changing after initial render
    const hasAspectRatio = aspectRatio !== null;
    return (_jsx("div", { ref: containerRef, children: _jsx(Document, { file: pdfUrl, onLoadSuccess: handleLoadSuccess, onLoadError: handleError, loading: _jsx(LoadingSpinner, { className: "py-4", size: "md" }), children: hasAspectRatio ? (_jsxs(_Fragment, { children: [topSpacerHeight > 0 && (_jsx("div", { style: { height: topSpacerHeight } })), Array.from({ length: visibleRange.end - visibleRange.start }, (_, index) => {
                        const pageNumber = visibleRange.start + index + 1;
                        return (_jsx("div", { "data-page-index": pageNumber - 1, style: { height: itemHeight, overflow: 'hidden' }, children: _jsx(VirtualizedThumbnail, { pageNumber: pageNumber, width: thumbnailWidth, isSelected: pageNumber === currentPage, onSelect: () => onPageSelect(pageNumber), renderThumbnail: renderThumbnail, aspectRatio: effectiveAspectRatio }) }, pageNumber));
                    }), bottomSpacerHeight > 0 && (_jsx("div", { style: { height: bottomSpacerHeight } }))] })) : (_jsx(LoadingSpinner, { className: "py-4", size: "md" })) }) }));
}
export function PdfDocumentRenderer({ pdfUrl, pageNumber, width, height, className, renderTextLayer = false, renderAnnotationLayer = false, onPageChange }) {
    const [numPages, setNumPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (numPages > 0) {
            onPageChange?.(pageNumber, numPages);
        }
    }, [pageNumber, numPages, onPageChange]);
    const handleLoadSuccess = ({ numPages: pages }) => {
        setNumPages(pages);
        setLoading(false);
    };
    const handleError = (err) => {
        setLoading(false);
        setError(err);
    };
    if (error) {
        return (_jsx("div", { className: `flex items-center justify-center text-destructive ${className || ''}`, children: _jsxs("span", { children: ["Failed to load PDF: ", error.message] }) }));
    }
    return (_jsxs("div", { className: className, children: [loading && (_jsx(LoadingSpinner, { className: "py-8", size: "lg" })), _jsx(Document, { file: pdfUrl, onLoadSuccess: handleLoadSuccess, onLoadError: handleError, loading: null, children: _jsx(Page, { pageNumber: Math.min(pageNumber, numPages || 1), width: width, height: height, renderTextLayer: renderTextLayer, renderAnnotationLayer: renderAnnotationLayer, loading: _jsx(LoadingSpinner, { className: "py-8", size: "md" }) }) })] }));
}
//# sourceMappingURL=PdfPageRenderer.js.map